// API error envelope — mirrors api-design.md §1 Error envelope and §4
// Error Vocabulary verbatim. Stable codes are typed; unknown codes are
// allowed (forward-compat) but must still be surfaced via the message.

export type ApiErrorCode =
  | 'unauthenticated'
  | 'forbidden'
  | 'not_found'
  | 'invalid_state_transition'
  | 'irreversible_status'
  | 'duplicate_identity'
  | 'validation_failed'
  | 'invalid_query'
  | 'invalid_cursor'
  | 'immutable_field'
  | 'end_date_before_start_date'
  | 'incomplete_sales_credit'
  | 'payment_schedule_mismatch'
  | 'no_assignments'
  | 'activation_blocked'
  | 'managed_credit_exceeds_100'
  | 'allocation_sum_mismatch'
  | 'active_assignments_exist'
  | 'last_admin_required'
  | 'person_owns_non_archived_agreements'
  | 'cannot_assign_member_as_owner'
  | 'payment_schedule_not_applicable_to_tm'
  | 'unsupported_idempotency_key'
  | 'request_too_large'
  | 'unsupported_media_type'
  | 'internal_error'
  | 'service_unavailable'
  // Network failure synthesized client-side; not from the server
  | 'network_error';

export interface ApiErrorBody {
  error_code: ApiErrorCode | string;
  message: string;
  details?: Record<string, unknown>;
  request_id?: string;
}

// Thrown by the API client. status === 0 indicates a synthesized
// network/transport error.
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: Record<string, unknown>;
  readonly requestId: string | undefined;

  constructor(status: number, body: ApiErrorBody) {
    super(body.message ?? `API error (${body.error_code})`);
    this.name = 'ApiError';
    this.status = status;
    this.code = body.error_code;
    this.details = body.details ?? {};
    this.requestId = body.request_id;
  }

  is(code: ApiErrorCode): boolean {
    return this.code === code;
  }
}

// Activation blocker shape (api-design §3 :activate composite case).
export interface ActivationBlocker {
  code:
    | 'incomplete_sales_credit'
    | 'payment_schedule_mismatch'
    | 'no_assignments';
  details: Record<string, unknown>;
}

// Helpers to read structured details safely.
export function getDuplicateAgreementId(err: ApiError): string | null {
  const v = err.details['conflicting_agreement_id'];
  return typeof v === 'string' ? v : null;
}
export function getDuplicateCompanyId(err: ApiError): string | null {
  const v = err.details['conflicting_company_id'];
  return typeof v === 'string' ? v : null;
}
export function getDuplicatePersonId(err: ApiError): string | null {
  const v = err.details['conflicting_person_id'];
  return typeof v === 'string' ? v : null;
}
export function getValidationField(err: ApiError): string | null {
  const v = err.details['field'];
  return typeof v === 'string' ? v : null;
}
export function getValidationFields(
  err: ApiError,
): Array<{ field: string; message: string }> {
  const raw = err.details['fields'];
  if (!Array.isArray(raw)) return [];
  const out: Array<{ field: string; message: string }> = [];
  for (const item of raw) {
    if (
      item &&
      typeof item === 'object' &&
      typeof (item as { field?: unknown }).field === 'string' &&
      typeof (item as { message?: unknown }).message === 'string'
    ) {
      out.push({
        field: (item as { field: string }).field,
        message: (item as { message: string }).message,
      });
    }
  }
  return out;
}
export function getActivationBlockers(err: ApiError): ActivationBlocker[] {
  const raw = err.details['blockers'];
  if (!Array.isArray(raw)) return [];
  const out: ActivationBlocker[] = [];
  for (const item of raw) {
    if (
      item &&
      typeof item === 'object' &&
      typeof (item as { code?: unknown }).code === 'string'
    ) {
      out.push(item as ActivationBlocker);
    }
  }
  return out;
}
export function getActiveAssignmentBlockers(err: ApiError): Array<{
  assignment_id: string;
  start_date: string;
  end_date: string | null;
}> {
  const raw = err.details['active_assignments'];
  if (!Array.isArray(raw)) return [];
  const out: Array<{ assignment_id: string; start_date: string; end_date: string | null }> = [];
  for (const item of raw) {
    if (
      item &&
      typeof item === 'object' &&
      typeof (item as { assignment_id?: unknown }).assignment_id === 'string'
    ) {
      const r = item as Record<string, unknown>;
      out.push({
        assignment_id: r.assignment_id as string,
        start_date: typeof r.start_date === 'string' ? r.start_date : '',
        end_date: typeof r.end_date === 'string' ? r.end_date : null,
      });
    }
  }
  return out;
}
export function getOwnedAgreementIds(err: ApiError): string[] {
  const raw = err.details['owned_agreement_ids'];
  return Array.isArray(raw) ? raw.filter((s): s is string => typeof s === 'string') : [];
}
